import lowerCase from "lodash.lowercase";

import {
  AudienceAction,
  AudienceFilterDto,
  CreateAudienceDto,
  ExperienceType,
  ExpressionNodeQueryType,
  ExpressionNodeType,
  OfferDto,
  QuantityButtonsUserInterface,
  RedirectDto,
} from "@intelligems/ig-types";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { hasStringDuplicates, postfixToInfix } from "@intelligems/ig-utils";
import { ProductPickerState } from "@src/store/slices/productPicker/types";

import { getSpecificRedirectsUtil } from "../components/redirects/getSpecificRedirects";

export const getDomainValue = (input: string, domain: string) => {
  let value: string;
  if (input === "") {
    value = input;
  } else if (domain.length === 0) {
    value = input;
  } else {
    try {
      const url = new URL(input);
      value = url.pathname.substring(1);
    } catch (e) {
      value = "";
    }
  }

  if (domain.length === 0) {
    return value;
  }

  value = value.split(`${domain}/`).pop() || "";
  value = value.includes(domain) ? "" : value;
  if (value.startsWith("/")) {
    value = value.substring(1);
  }
  return value;
};

export const shippingRateValidator = (variations: VariationFe[]) => {
  if (variations) {
    const isCustomRate = variations.some((group) =>
      group.shippingRateGroups!.some(
        (rateGroup) => rateGroup.groupType === "custom"
      )
    );
    if (isCustomRate) {
      return {
        isDisabled: false,
        reason: null,
      };
    } else {
      const hasEmptyShippingRates = variations?.some(
        (group) => group.shippingRateGroups!.length < 1
      );
      return {
        isDisabled: hasEmptyShippingRates,
        reason: hasEmptyShippingRates
          ? "Create rates for each test group"
          : null,
      };
    }
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

const renderQueryText = (query: ExpressionNodeQueryType) => {
  if (query.key === "screenLayout" || query.key === "device") {
    return `Device type is ${query.value}`;
  } else if (query.key === "visitor") {
    return `Visitor is ${query.value}`;
  } else if (query.key === "jsExpression") {
    return `(${query.value}) evaluates to true`;
  }

  return `${query.key} ${lowerCase(query.filter || "")} ${
    query.filter !== "isNotNull" && query.filter !== "isNull" ? query.value : ""
  }`;
};

export const renderConditionText = (expression: ExpressionNodeType[]) => {
  if (!expression || !expression.length) {
    return ""; // Return empty string for empty expressions
  }

  if (expression.length > 1) {
    const infix = postfixToInfix(expression);
    let result = "";
    for (const node of infix) {
      if (node.operator) {
        if (node.operator === "start_paren") result += "(";
        else if (node.operator === "end_paren") result += ")";
        else result += ` ${node.operator} `;
      } else if (node.query) {
        result += `${renderQueryText(node.query)}`;
      }
    }
    return result;
  }

  if (!expression[0].query) {
    return ""; // Return empty string if query is missing
  }

  let keyText = expression[0].query.key;
  if (
    expression[0].query.key === "screenLayout" ||
    expression[0].query.key === "device"
  ) {
    keyText = "Device type";
  } else if (expression[0].query.key === "visitor") {
    keyText = "Visitor is";
  }

  if (expression[0].query.key === "visitor") {
    return `${keyText} is ${expression[0].query.value}`;
  }

  if (expression[0].query.key === "jsExpression") {
    return `(${expression[0].query.value}) evaluates to true`;
  }

  return `${keyText} ${lowerCase(expression[0].query.filter || "")} ${
    expression[0].query.filter !== "isNotNull" &&
    expression[0].query.filter !== "isNull"
      ? expression[0].query.value
      : ""
  } `;
};

export const renderTargetExperimentBehavior = (
  variations: VariationFe[],
  queryParam: AudienceFilterDto,
  entity: "experiment" | "campaign"
) => {
  switch (queryParam.action as AudienceAction) {
    case "experienceInclude":
      return `include in ${entity}`;

    case "randomVariation":
      return "assign to random group";
    case "assignVariation":
      const assignedGroup = variations.find(
        (group) => group.id === queryParam.variationId
      );
      if (assignedGroup) {
        return `assign to ${assignedGroup.name}${
          queryParam.excludeFromAnalytics ? " and exclude from analytics" : ""
        }`;
      }
      return `assign to specific group${
        queryParam.excludeFromAnalytics ? " and exclude from analytics" : ""
      }`;
    case "experienceExclude":
      return `exclude from ${entity}`;
    case "experienceUnassigned":
      return "leave unassigned";
    case "leaveUnassigned":
      return "leave unassigned";
    default:
      return "assign to random group";
  }
};

export const urlRedirectValidator = (
  contentFlowType: ExperienceType,
  specificRedirectErrors: string[],
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  if (specificRedirectErrors.length > 0) {
    return {
      isDisabled: true,
      reason: "Fix all redirect errors before continuing",
    };
  }

  const urlRedirects = getSpecificRedirectsUtil(redirectsState, "url");
  if (contentFlowType === "content/url" || urlRedirects.length) {
    if (urlRedirects.length === 0) {
      return { isDisabled: true, reason: "Create URL redirects" };
    }
    if (
      urlRedirects.some(
        (redirect) =>
          redirect.queryParams.length > 0 &&
          redirect.queryParams.some(
            (queryParam) => !queryParam.key || queryParam.key.length === 0
          )
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect query parameters",
      };
    }

    if (
      urlRedirects.some(
        (redirect) =>
          redirect.originUrl.length === 0 ||
          (!redirect.destinationUrl && !redirect.skip) ||
          (redirect.destinationUrl &&
            redirect.destinationUrl.length === 0 &&
            !redirect.skip) ||
          (redirect.redirectType === "partialReplacement" &&
            !redirect.skip &&
            (!redirect.find || redirect.find.length === 0))
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect URL inputs",
      };
    }
  }

  return { isDisabled: false, reason: null };
};

export const templateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const templateRedirects = redirectsState.filter(
    (r) => r.isTemplateTest && !r.skip
  );
  if (templateRedirects.length) {
    for (const templateRedirect of templateRedirects) {
      if (!templateRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a template for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/template") {
    const hasNoTemplateRedirects =
      getSpecificRedirectsUtil(redirectsState, "template").length === 0;
    return {
      isDisabled: hasNoTemplateRedirects,
      reason: hasNoTemplateRedirects
        ? "Select a template for each group"
        : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const themeRedirects = redirectsState.filter((r) => r.isThemeTest && !r.skip);
  if (themeRedirects.length) {
    for (const themeRedirect of themeRedirects) {
      if (!themeRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a theme for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/theme") {
    const hasNoThemeRedirects =
      getSpecificRedirectsUtil(redirectsState, "theme").length === 0;
    return {
      isDisabled: hasNoThemeRedirects,
      reason: hasNoThemeRedirects ? "Select a theme for each group" : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeOrTemplateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const isThemeNotValid = themeValidator(contentFlowType, redirectsState);
  const isTemplateNotValid = templateValidator(contentFlowType, redirectsState);
  if (isThemeNotValid.isDisabled) {
    return isThemeNotValid;
  }
  if (isTemplateNotValid.isDisabled) {
    return isTemplateNotValid;
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const experienceDetailsValidator = (
  experience: ExperienceFe,
  productPickerProductsLength: number
): { isDisabled: boolean; reason: string | null } => {
  if (!experience?.name) {
    return {
      isDisabled: true,
      reason: "Provide a name for this experiment",
    };
  }
  if (!experience?.experienceAnalysis.goal && !experience.endedAtTs) {
    return {
      isDisabled: true,
      reason: "Provide a goal for this experiment",
    };
  }
  if (
    experience.testTypes.hasTestPricing &&
    (!experience.experienceAnalysis.analyticsViewType ||
      experience.experienceAnalysis.analyticsViewType === "none") &&
    !experience.endedAtTs
  ) {
    return {
      isDisabled: true,
      reason: "Select a default view for analysis",
    };
  }

  if (
    experience.type.includes("content") &&
    experience.experienceAnalysis.analyticsViewType === "test_orders_only" &&
    productPickerProductsLength === 0
  ) {
    return {
      isDisabled: true,
      reason:
        "Select at least one product to target or switch how you would like to measure conversion to all orders",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const variationDetailsValidator = (
  variations: VariationFe[]
): { isDisabled: boolean; reason: string | null } => {
  if ((variations || []).length <= 1) {
    return {
      isDisabled: true,
      reason: "Create at least two test groups",
    };
  }
  if (hasStringDuplicates((variations || []).map((tg) => tg.name))) {
    return {
      isDisabled: true,
      reason: "Provide a unique name for each test group",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const productPickerValidator = (
  productPickerState: ProductPickerState,
  id: string | undefined
) => {
  if (id) {
    return {
      isDisabled: false,
      reason: null,
    };
  } else {
    const noProductsSelected = productPickerState.selectedProducts.length === 0;

    return {
      isDisabled: noProductsSelected,
      reason: noProductsSelected ? "Select at least one product" : null,
    };
  }
};

export const googleAnalyticsValidator = (
  isGoogleAnalyticsNextDisabled: boolean
) => {
  return {
    isDisabled: isGoogleAnalyticsNextDisabled,
    reason: isGoogleAnalyticsNextDisabled
      ? "Allow Google Analytics to load before continuing"
      : null,
  };
};

export const isAudienceTargetingEnabled = (audience: CreateAudienceDto) => {
  return audience.filters.length > 0;
};

export const offerValidator = (
  offers: OfferDto[],
  variations?: VariationFe[]
): { isDisabled: boolean; reason: string | null } => {
  if (offers.length === 0) {
    return { isDisabled: false, reason: null };
  }

  const offersToValidate = variations ? offers : [offers[0]];

  for (const offer of offersToValidate) {
    if (!offer.name || offer.name.trim().length === 0) {
      return {
        isDisabled: true,
        reason: "Offer name is required",
      };
    }

    switch (offer.discountApplicationType) {
      case "cartDiscount":
        const firstTier = offer.tiers[0];
        if (!firstTier || firstTier.unitDiscount === 0) {
          return {
            isDisabled: true,
            reason: "Discount value cannot be 0",
          };
        }
        break;

      case "tieredDiscount":
        if (offer.tiers.length < 2) {
          return {
            isDisabled: true,
            reason: "At least 2 tiers are required for volume discounts",
          };
        }

        if (
          offer.tiers.length !==
          new Set(offer.tiers.map((t) => t.minimumUnits)).size
        ) {
          return {
            isDisabled: true,
            reason: `Each tier must have a unique ${offer.unitType === "unit" ? "item quantity" : "subtotal"}`,
          };
        }
        for (let i = 0; i < offer.tiers.length; i++) {
          const tier = offer.tiers[i];
          if (tier.minimumUnits <= 0) {
            return {
              isDisabled: true,
              reason: `Offer tier ${i + 1} requires a minimum purchase ${offer.unitType === "unit" ? "quantity" : "subtotal"}`,
            };
          }
          if (tier.unitDiscount < 0) {
            return {
              isDisabled: true,
              reason: `Tier ${i + 1} requires a discount value`,
            };
          }
        }
        break;

      case "freeGift":
        const hasGiftProduct = offer.tiers.some(
          (tier) => tier.isGiftWithPurchase && tier.giftWithPurchaseProductId
        );

        if (!hasGiftProduct) {
          return {
            isDisabled: true,
            reason: "A free gift product must be selected",
          };
        }
        break;
    }
  }

  return { isDisabled: false, reason: null };
};

export const quantityButtonValidator = (
  quantityButtons: QuantityButtonsUserInterface
) => {
  if (
    quantityButtons.widget?.config.desktop?.variant !==
    "quantityButtonsWidgetImage"
  ) {
    return {
      isDisabled: false,
      reason: null,
    };
  }
  const buttons = quantityButtons.desktopVariables?.buttons || [];
  if (buttons.length !== buttons.filter((button) => !!button.image).length) {
    return {
      isDisabled: true,
      reason: "Each button must have an image",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};
